import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { CommentsPanel } from '../components/CommentsPanel.tsx'
import { SendToChatDialog } from '../components/SendToChatDialog.tsx'

/**
 * ЧТО ПОСМОТРЕТЬ
 *
 * Общий список семьи. Нужен он ровно в один момент — вечером, когда
 * сели и не могут вспомнить, что собирались смотреть. Поэтому «хотим
 * посмотреть» открывается первым, а просмотренное убрано на вторую
 * вкладку: там оно нужно раз в полгода, для спора «смотрели или нет».
 */

type Movie = {
  id: string
  title: string
  originalTitle: string | null
  kind: string
  year: number | null
  description: string | null
  status: string
  rating: number | null
  watchedAt: string | null
  addedBy: { id: string; displayName: string; avatarUrl: string | null } | null
  mine: boolean
}

const KINDS: Record<string, string> = {
  MOVIE: 'Фильм',
  SERIES: 'Сериал',
  CARTOON: 'Мультфильм',
}

const KIND_ICONS: Record<string, string> = {
  MOVIE: '🎬',
  SERIES: '📺',
  CARTOON: '🧸',
}

export function MoviesPage() {
  const [tab, setTab] = useState<'want' | 'watched'>('want')
  const [items, setItems] = useState<Movie[] | null>(null)
  const [draft, setDraft] = useState<Partial<Movie> | null>(null)
  const [open, setOpen] = useState<Movie | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ items: Movie[] }>('/api/media')
      .then((data) => setItems(data.items))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [])

  const setStatus = async (movie: Movie, status: string) => {
    setItems((current) =>
      (current ?? []).map((m) => (m.id === movie.id ? { ...m, status } : m)),
    )
    await api(`/api/media/${movie.id}`, { method: 'PATCH', body: { status } }).catch(load)
  }

  const want = (items ?? []).filter((m) => m.status === 'WANT' || m.status === 'WATCHING')
  const watched = (items ?? []).filter((m) => m.status === 'WATCHED' || m.status === 'DROPPED')
  const shown = tab === 'want' ? want : watched

  return (
    <div className="section">
      <header className="section-head">
        <h1>Кино</h1>
        <button
          className="icon-btn"
          onClick={() => setDraft({ title: '', kind: 'MOVIE' })}
          aria-label="Добавить"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      <div className="planner-tabs">
        <button
          className={'tab-btn' + (tab === 'want' ? ' is-active' : '')}
          onClick={() => setTab('want')}
        >
          Посмотреть{want.length > 0 && ` · ${want.length}`}
        </button>
        <button
          className={'tab-btn' + (tab === 'watched' ? ' is-active' : '')}
          onClick={() => setTab('watched')}
        >
          Посмотрели
        </button>
      </div>

      <div className="section-body scroll">
        {shown.length === 0 && (
          <p className="empty-hint">
            {tab === 'want'
              ? 'Пусто. Добавь то, что хотите посмотреть вместе.'
              : 'Пока ничего не отмечено просмотренным.'}
          </p>
        )}

        {shown.map((movie) => (
          <div key={movie.id} className="movie-row">
            <button className="movie-main" onClick={() => setOpen(movie)}>
              <span className="movie-icon" aria-hidden="true">
                {KIND_ICONS[movie.kind] ?? '🎬'}
              </span>

              <span className="movie-text">
                <span className="movie-title">
                  {movie.title}
                  {movie.year && <span className="movie-year">{movie.year}</span>}
                </span>
                <span className="movie-sub">
                  <span>{KINDS[movie.kind] ?? movie.kind}</span>
                  {movie.addedBy && <span>предложил{'а'.repeat(0)} {movie.addedBy.displayName}</span>}
                </span>
              </span>
            </button>

            {movie.addedBy && (
              <Avatar
                id={movie.addedBy.id}
                name={movie.addedBy.displayName}
                url={movie.addedBy.avatarUrl}
                size={22}
              />
            )}

            <button
              className={'rsvp-btn' + (movie.status === 'WATCHED' ? ' is-yes' : '')}
              onClick={() => void setStatus(movie, movie.status === 'WATCHED' ? 'WANT' : 'WATCHED')}
              title={movie.status === 'WATCHED' ? 'Вернуть в список' : 'Посмотрели'}
            >
              ✓
            </button>
          </div>
        ))}

        {error && <div className="sheet-error">{error}</div>}
      </div>

      {draft && <MovieSheet draft={draft} onClose={() => setDraft(null)} onSaved={load} />}

      {open && (
        <MovieDetails
          movie={open}
          onClose={() => setOpen(null)}
          onEdit={() => {
            setDraft(open)
            setOpen(null)
          }}
          onChanged={load}
        />
      )}
    </div>
  )
}

function MovieDetails({
  movie,
  onClose,
  onEdit,
  onChanged,
}: {
  movie: Movie
  onClose: () => void
  onEdit: () => void
  onChanged: () => void
}) {
  const [sending, setSending] = useState(false)
  const [rating, setRating] = useState(movie.rating ?? 0)

  const rate = async (value: number) => {
    setRating(value)
    await api(`/api/media/${movie.id}`, { method: 'PATCH', body: { rating: value } })
    onChanged()
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{movie.title}</h2>
          <button className="icon-btn" onClick={() => setSending(true)} title="Отправить в переписку">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
            </svg>
          </button>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <p className="sheet-text">
          {KINDS[movie.kind] ?? movie.kind}
          {movie.year && ` · ${movie.year}`}
          {movie.originalTitle && ` · ${movie.originalTitle}`}
        </p>

        {movie.description && <p className="record-summary">{movie.description}</p>}

        {movie.status === 'WATCHED' && (
          <>
            <h3 className="sheet-sub">Как тебе</h3>
            <div className="rating-row">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={'star' + (rating >= star * 2 ? ' is-on' : '')}
                  onClick={() => void rate(star * 2)}
                  aria-label={`${star} из 5`}
                >
                  ★
                </button>
              ))}
            </div>
            <p className="sheet-note">Оценка своя у каждого — спорить приложение не будет.</p>
          </>
        )}

        <h3 className="sheet-sub">Обсуждение</h3>
        <CommentsPanel entityType="MEDIA_ITEM" entityId={movie.id} />

        {movie.mine && (
          <div className="sheet-actions">
            <button className="btn-primary" onClick={onEdit}>
              Изменить
            </button>
          </div>
        )}

        {sending && (
          <SendToChatDialog
            entityType="MEDIA_ITEM"
            entityId={movie.id}
            title={movie.title}
            onClose={() => setSending(false)}
          />
        )}
      </div>
    </div>
  )
}

function MovieSheet({
  draft,
  onClose,
  onSaved,
}: {
  draft: Partial<Movie>
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)

  const [title, setTitle] = useState(draft.title ?? '')
  const [kind, setKind] = useState(draft.kind ?? 'MOVIE')
  const [year, setYear] = useState(draft.year?.toString() ?? '')
  const [description, setDescription] = useState(draft.description ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        title: title.trim(),
        kind,
        year: year.trim() ? Number(year) : null,
        description: description.trim() || null,
      }

      if (draft.id) {
        await api(`/api/media/${draft.id}`, { method: 'PATCH', body })
      } else {
        await api('/api/media', { method: 'POST', body })
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!draft.id || !window.confirm('Убрать из списка?')) return
    setBusy(true)
    try {
      await api(`/api/media/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось убрать')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? 'Правка' : 'Что посмотреть'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Название</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ирония судьбы"
            maxLength={200}
            autoFocus={!editing}
          />
        </label>

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Что это</span>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
              {Object.entries(KINDS).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field-label">Год</span>
            <input
              className="input"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="Не обязательно"
              inputMode="numeric"
              maxLength={4}
            />
          </label>
        </div>

        <label className="field">
          <span className="field-label">О чём</span>
          <textarea
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Пара слов, чтобы через месяц вспомнить, почему записали"
            maxLength={2000}
          />
        </label>

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button className="btn-primary" disabled={busy || !title.trim()} onClick={() => void save()}>
            {busy ? '…' : 'Сохранить'}
          </button>
          {editing && (
            <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
              Убрать
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
